/**
 * PerformanceDashboard Component
 *
 * A comprehensive performance view inspired by Vanguard's interface.
 * Features sub-tabs for Graph, Calculations, Month, and Investments views.
 */

import React, { useState, useMemo } from 'react';
import { Calendar, TrendingUp, TrendingDown, BarChart3, Table2, LineChart, Calculator, Info } from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { useCountry } from '../../CountryContext';
import { getCurrencyCode, getCurrencySymbol, formatCurrency as formatCurrencyConfig } from '../../countryConfig';
import { usePortfolioPrices } from '../../hooks/usePricing';

const PerformanceDashboard = ({
  investments = [],
  wealthSnapshots = [],
  totalValue = 0,
  totalCost = 0,
  totalContributions = 0,
  investmentReturns = 0,
  isDark = false,
  exchangeRates = null
}) => {
  const { selectedCountry } = useCountry();
  const displayCurrency = getCurrencyCode(selectedCountry);
  const currencySymbol = getCurrencySymbol(selectedCountry);

  // Fetch real-time prices for investments
  const { prices } = usePortfolioPrices(investments);

  const [activeSubTab, setActiveSubTab] = useState('graph');
  const [dateRange, setDateRange] = useState('inception'); // inception, 1y

  // Get the earliest snapshot date (platform start date)
  const platformStartDate = useMemo(() => {
    if (!wealthSnapshots || wealthSnapshots.length === 0) return null;
    const sorted = [...wealthSnapshots].sort((a, b) => a.month.localeCompare(b.month));
    const [year, month] = sorted[0].month.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, 1);
  }, [wealthSnapshots]);

  // State for tooltip visibility
  const [activeTooltip, setActiveTooltip] = useState(null);

  // Calculate performance metrics based on snapshots
  const metrics = useMemo(() => {
    // For "Since Inception" - use cost-based calculation
    // This is more accurate as it uses actual cost basis of investments
    if (dateRange === 'inception' || !wealthSnapshots || wealthSnapshots.length === 0) {
      const startingValue = 0; // Started with nothing
      const contributedWithdrew = totalCost; // Total cost basis = total invested
      const returnsAmount = totalValue - totalCost; // Gain/loss on investments
      const endingValue = totalValue;
      const rateOfReturn = totalCost > 0 ? (returnsAmount / totalCost) * 100 : 0;

      return {
        startingValue,
        contributedWithdrew,
        returnsAmount,
        endingValue,
        rateOfReturn
      };
    }

    // For "1 Year" - use snapshot-based calculation
    // Sort snapshots chronologically
    const sorted = [...wealthSnapshots].sort((a, b) => a.month.localeCompare(b.month));

    // Get snapshot from 1 year ago
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const oneYearAgoMonth = `${oneYearAgo.getFullYear()}-${String(oneYearAgo.getMonth() + 1).padStart(2, '0')}`;

    // Find the snapshot closest to 1 year ago (before or on that date)
    let startSnapshot = null;
    for (let i = sorted.length - 1; i >= 0; i--) {
      if (sorted[i].month <= oneYearAgoMonth) {
        startSnapshot = sorted[i];
        break;
      }
    }

    // If no snapshot from 1 year ago, use earliest available
    if (!startSnapshot) {
      startSnapshot = sorted[0];
    }

    const startingValue = startSnapshot?.netWorth || startSnapshot?.totalAssets || 0;

    // Sum contributions and withdrawals for snapshots after the start date
    const filteredSnapshots = sorted.filter(s => s.month > (startSnapshot?.month || ''));
    let totalContributionsInPeriod = 0;
    let totalWithdrawalsInPeriod = 0;
    filteredSnapshots.forEach(snapshot => {
      if (snapshot.changes) {
        totalContributionsInPeriod += snapshot.changes.newContributions || 0;
        totalWithdrawalsInPeriod += snapshot.changes.withdrawals || 0;
      }
    });

    const contributedWithdrew = totalContributionsInPeriod - totalWithdrawalsInPeriod;
    const endingValue = totalValue;

    // Investment returns = Ending value - Starting value - Net contributions
    const returnsAmount = endingValue - startingValue - contributedWithdrew;

    // Rate of return using modified Dietz method
    const avgInvestment = startingValue + (contributedWithdrew / 2);
    const rateOfReturn = avgInvestment > 0
      ? (returnsAmount / avgInvestment) * 100
      : (startingValue > 0 ? (returnsAmount / startingValue) * 100 : 0);

    return {
      startingValue,
      contributedWithdrew,
      returnsAmount,
      endingValue,
      rateOfReturn
    };
  }, [totalValue, totalCost, wealthSnapshots, dateRange]);

  // Process snapshots for chart
  const chartData = useMemo(() => {
    if (!wealthSnapshots || wealthSnapshots.length === 0) {
      // If no snapshots but we have current value, show just current point
      if (totalValue > 0) {
        const now = new Date();
        return [{
          month: now.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
          value: totalValue,
          label: now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        }];
      }
      return [];
    }

    // Sort snapshots by date and add current value as the latest point
    const sorted = [...wealthSnapshots].sort((a, b) => a.month.localeCompare(b.month));
    const data = sorted.map(snapshot => {
      const [year, month] = snapshot.month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return {
        month: date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        value: snapshot.netWorth || snapshot.totalAssets || 0,
        label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      };
    });

    // Add current value as the latest data point if it's after the last snapshot
    if (totalValue > 0) {
      const now = new Date();
      const currentMonth = now.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      const lastDataMonth = data.length > 0 ? data[data.length - 1].month : '';

      if (currentMonth !== lastDataMonth) {
        data.push({
          month: currentMonth,
          value: totalValue,
          label: now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        });
      }
    }

    return data;
  }, [wealthSnapshots, totalValue]);

  // Monthly breakdown data
  const monthlyData = useMemo(() => {
    if (!wealthSnapshots || wealthSnapshots.length === 0) return [];

    const sorted = [...wealthSnapshots].sort((a, b) => b.month.localeCompare(a.month));

    return sorted.slice(0, 12).map((snapshot, idx) => {
      const prev = sorted[idx + 1];
      const [year, month] = snapshot.month.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);

      const beginningBalance = prev?.netWorth || prev?.totalAssets || 0;
      const endingBalance = snapshot.netWorth || snapshot.totalAssets || 0;
      const contributions = snapshot.changes?.newContributions || 0;
      const withdrawals = snapshot.changes?.withdrawals || 0;
      const marketGainLoss = snapshot.changes?.marketGrowth || (endingBalance - beginningBalance - contributions + withdrawals);
      const incomeReturns = snapshot.changes?.dividends || 0;
      const fees = snapshot.changes?.fees || 0;
      const personalReturns = marketGainLoss + incomeReturns - fees;

      // Calculate cumulative returns (simplified)
      let cumulativeReturns = 0;
      for (let i = sorted.length - 1; i >= idx; i--) {
        cumulativeReturns += sorted[i].changes?.marketGrowth || 0;
      }

      return {
        month: date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        beginningBalance,
        purchasesWithdrawals: contributions - withdrawals,
        marketGainLoss,
        incomeReturns,
        fees,
        personalReturns,
        cumulativeReturns,
        endingBalance
      };
    });
  }, [wealthSnapshots]);

  // Calculations breakdown
  const calculations = useMemo(() => {
    // Sum up all contributions and investment returns from snapshots
    let cashPaidIn = 0;
    let cashWithdrawn = 0;
    let investmentsSold = 0;
    let grossInterest = 0;
    let dividends = 0;
    let tax = 0;
    let charges = 0;

    // Filter snapshots based on date range
    let filteredSnapshots = wealthSnapshots;
    if (dateRange === '1y' && wealthSnapshots.length > 0) {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const oneYearAgoMonth = `${oneYearAgo.getFullYear()}-${String(oneYearAgo.getMonth() + 1).padStart(2, '0')}`;
      filteredSnapshots = wealthSnapshots.filter(s => s.month >= oneYearAgoMonth);
    }

    filteredSnapshots.forEach(snapshot => {
      if (snapshot.changes) {
        cashPaidIn += snapshot.changes.newContributions || 0;
        cashWithdrawn += Math.abs(snapshot.changes.withdrawals || 0);
        investmentsSold += snapshot.changes.investmentsSold || 0;
        grossInterest += snapshot.changes.interestPaid || 0;
        dividends += snapshot.changes.dividends || 0;
        tax += snapshot.changes.tax || 0;
        charges += snapshot.changes.charges || 0;
      }
    });

    // If no snapshot data, use totals for contributions
    if (cashPaidIn === 0 && dateRange === 'inception') {
      cashPaidIn = totalContributions;
    }

    const gainLoss = totalValue - totalCost;
    const totalReturns = gainLoss + investmentsSold + grossInterest + dividends - tax - charges;

    return {
      contributed: {
        cashPaidIn,
        cashWithdrawn,
        total: cashPaidIn - cashWithdrawn
      },
      returns: {
        gainLoss,
        investmentsSold,
        grossInterest,
        dividends,
        tax,
        charges,
        total: totalReturns
      }
    };
  }, [wealthSnapshots, totalContributions, totalValue, totalCost, dateRange]);

  const formatValue = (value) => {
    return formatCurrencyConfig(value, selectedCountry);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    const sign = value >= 0 ? '' : '';
    return `${value.toFixed(2)}%`;
  };

  // Sub-tab buttons styling
  const subTabClass = (tab) => `
    px-4 py-2.5 text-sm font-medium border-b-2 transition-colors
    ${activeSubTab === tab
      ? isDark
        ? 'border-blue-400 text-blue-400 bg-blue-900/20'
        : 'border-blue-600 text-blue-600 bg-blue-50'
      : isDark
        ? 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
    }
  `;

  return (
    <div className={`rounded-xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
      {/* Date Range Selector */}
      <div className={`p-4 border-b ${isDark ? 'border-gray-700 bg-gray-900/30' : 'border-gray-200 bg-gray-50'} rounded-t-xl`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Start:</span>
              <span className={`px-3 py-1.5 rounded border text-center min-w-[100px] ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'}`}>
                {dateRange === 'inception' && platformStartDate
                  ? platformStartDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : dateRange === '1y'
                    ? new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : platformStartDate
                      ? platformStartDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                      : 'No data'
                }
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>End:</span>
              <span className={`px-3 py-1.5 rounded border text-center min-w-[100px] ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-100 border-gray-300 text-gray-900'}`}>
                {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setDateRange('inception')}
              className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${
                dateRange === 'inception'
                  ? 'bg-blue-600 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Since Inception
            </button>
            <button
              onClick={() => setDateRange('1y')}
              className={`px-4 py-1.5 text-sm font-medium rounded transition-colors ${
                dateRange === '1y'
                  ? 'bg-blue-600 text-white'
                  : isDark
                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              1 Year
            </button>
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className={`p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
          {/* Left side - Starting values */}
          <div className="text-center lg:text-right space-y-2">
            <div className="relative">
              <div className="flex items-center justify-center lg:justify-end gap-1">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You started with</span>
                <button
                  onClick={() => setActiveTooltip(activeTooltip === 'started' ? null : 'started')}
                  className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
              {activeTooltip === 'started' && (
                <div className={`absolute z-10 p-3 rounded-lg shadow-lg text-sm max-w-xs right-0 mt-1 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                  {dateRange === 'inception'
                    ? 'Your starting portfolio value when you first began investing (£0 as you started from nothing).'
                    : 'Your portfolio value at the start of the selected period (1 year ago).'}
                </div>
              )}
              <p className="text-xl font-bold text-green-600">{formatValue(metrics.startingValue)}</p>
            </div>
            <div className="relative">
              <div className="flex items-center justify-center lg:justify-end gap-1">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You contributed and withdrew</span>
                <button
                  onClick={() => setActiveTooltip(activeTooltip === 'contributed' ? null : 'contributed')}
                  className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
              {activeTooltip === 'contributed' && (
                <div className={`absolute z-10 p-3 rounded-lg shadow-lg text-sm max-w-xs right-0 mt-1 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                  {dateRange === 'inception'
                    ? 'The total cost basis of all your investments - the total amount you have invested over time.'
                    : 'Net money added (contributions minus withdrawals) during the selected period.'}
                </div>
              )}
              <p className="text-xl font-bold text-green-600">{formatValue(metrics.contributedWithdrew)}</p>
            </div>
            <div className="relative">
              <div className="flex items-center justify-center lg:justify-end gap-1">
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Your investments returned you</span>
                <button
                  onClick={() => setActiveTooltip(activeTooltip === 'returns' ? null : 'returns')}
                  className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Info className="w-4 h-4" />
                </button>
              </div>
              {activeTooltip === 'returns' && (
                <div className={`absolute z-10 p-3 rounded-lg shadow-lg text-sm max-w-xs right-0 mt-1 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                  Your investment gains or losses: Current value minus starting value minus contributions. This shows how much your investments have grown (or declined) due to market performance.
                </div>
              )}
              <p className={`text-xl font-bold ${metrics.returnsAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatValue(metrics.returnsAmount)}
              </p>
            </div>
          </div>

          {/* Center bracket */}
          <div className="text-4xl text-gray-300">{'}'}</div>

          {/* Right side - Ending values */}
          <div className="text-center lg:text-left space-y-2">
            <div>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>You ended up with</span>
              <p className="text-3xl font-bold text-green-600">{formatValue(metrics.endingValue)}</p>
            </div>
          </div>

          {/* Rate of Return */}
          <div className="text-center lg:text-left relative">
            <div className="flex items-center gap-1">
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Your rate of return is</span>
              <button
                onClick={() => setActiveTooltip(activeTooltip === 'rate' ? null : 'rate')}
                className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <Info className="w-4 h-4" />
              </button>
            </div>
            {activeTooltip === 'rate' && (
              <div className={`absolute z-10 p-3 rounded-lg shadow-lg text-sm max-w-xs left-0 mt-1 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                {dateRange === 'inception'
                  ? 'Your total return on investment since you started: (Current value - Total invested) / Total invested × 100'
                  : 'Your return for the period, calculated using the Modified Dietz method which accounts for the timing of your contributions.'}
              </div>
            )}
            <p className={`text-3xl font-bold ${metrics.rateOfReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {metrics.rateOfReturn >= 0 ? '+' : ''}{formatPercent(metrics.rateOfReturn)}
            </p>
          </div>
        </div>
      </div>

      {/* Click outside to close tooltip */}
      {activeTooltip && (
        <div className="fixed inset-0 z-0" onClick={() => setActiveTooltip(null)} />
      )}

      {/* Sub-tabs */}
      <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex">
          <button onClick={() => setActiveSubTab('graph')} className={subTabClass('graph')}>
            <span className="flex items-center gap-2">
              <LineChart className="w-4 h-4" />
              Graph
            </span>
          </button>
          <button onClick={() => setActiveSubTab('calculations')} className={subTabClass('calculations')}>
            <span className="flex items-center gap-2">
              <Calculator className="w-4 h-4" />
              Calculations
            </span>
          </button>
          <button onClick={() => setActiveSubTab('month')} className={subTabClass('month')}>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Month
            </span>
          </button>
          <button onClick={() => setActiveSubTab('investments')} className={subTabClass('investments')}>
            <span className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Investments
            </span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6">
        {/* Graph Tab */}
        {activeSubTab === 'graph' && (
          <div className="h-80">
            {chartData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#e5e7eb'} />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    axisLine={{ stroke: isDark ? '#4b5563' : '#d1d5db' }}
                  />
                  <YAxis
                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    tickFormatter={(value) => `${currencySymbol}${(value / 1000).toFixed(0)}k`}
                    axisLine={{ stroke: isDark ? '#4b5563' : '#d1d5db' }}
                    domain={['auto', 'auto']}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#1f2937' : '#ffffff',
                      border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: isDark ? '#f3f4f6' : '#111827' }}
                    formatter={(value) => [formatValue(value), 'Portfolio Value']}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorValue)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className={`h-full flex flex-col items-center justify-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <LineChart className="w-16 h-16 mb-4 opacity-50" />
                <p className="font-medium text-lg">Not enough data for graph</p>
                <p className="text-sm mt-2 text-center max-w-md">
                  Create monthly wealth snapshots to track your portfolio value over time.
                  {chartData.length === 1 && (
                    <span className="block mt-2">
                      Current value: <span className="font-semibold text-blue-600">{formatValue(chartData[0].value)}</span>
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Calculations Tab */}
        {activeSubTab === 'calculations' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Contributed and Withdrawn */}
            <div>
              <div className="flex items-center gap-2 mb-4 relative">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Contributed and withdrawn
                </h3>
                <button
                  onClick={() => setActiveTooltip(activeTooltip === 'calc-contrib' ? null : 'calc-contrib')}
                  className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Info className="w-4 h-4" />
                </button>
                {activeTooltip === 'calc-contrib' && (
                  <div className={`absolute z-10 p-3 rounded-lg shadow-lg text-sm max-w-xs left-0 top-6 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                    Breakdown of cash you've paid into your investments, withdrawn, and any transfers between accounts.
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Cash paid in</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatValue(calculations.contributed.cashPaidIn)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Cash withdrawn</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatValue(calculations.contributed.cashWithdrawn)}</span>
                </div>
                <div className={`flex justify-between pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatValue(calculations.contributed.total)}</span>
                </div>
              </div>
            </div>

            {/* Your Investments Returned */}
            <div>
              <div className="flex items-center gap-2 mb-4 relative">
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Your investments returned
                </h3>
                <button
                  onClick={() => setActiveTooltip(activeTooltip === 'calc-returns' ? null : 'calc-returns')}
                  className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Info className="w-4 h-4" />
                </button>
                {activeTooltip === 'calc-returns' && (
                  <div className={`absolute z-10 p-3 rounded-lg shadow-lg text-sm max-w-xs left-0 top-6 ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-white text-gray-700 border border-gray-200'}`}>
                    Breakdown of your investment returns including unrealised gains/losses, sold investments, interest, dividends, taxes and fees.
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Current investment value gain/loss</span>
                  <span className={calculations.returns.gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {formatValue(calculations.returns.gainLoss)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Investments sold</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatValue(calculations.returns.investmentsSold)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Gross interest</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatValue(calculations.returns.grossInterest)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Dividends</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatValue(calculations.returns.dividends)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Tax</span>
                  <span className={isDark ? 'text-white' : 'text-gray-900'}>{formatValue(calculations.returns.tax)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Charges</span>
                  <span className="text-red-600">-{formatValue(Math.abs(calculations.returns.charges))}</span>
                </div>
                <div className={`flex justify-between pt-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</span>
                  <span className={`font-semibold ${calculations.returns.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatValue(calculations.returns.total)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Month Tab */}
        {activeSubTab === 'month' && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className={`px-3 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Month</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Beginning balance</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Purchases & Withdrawals</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Market Gain/Loss</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Income returns</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Fees</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Personal investments returns</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Cumulative returns</th>
                  <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Ending balance</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.length > 0 ? monthlyData.map((row, idx) => (
                  <tr key={idx} className={`border-b ${isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className={`px-3 py-3 text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{row.month}</td>
                    <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{formatValue(row.beginningBalance)}</td>
                    <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{formatValue(row.purchasesWithdrawals)}</td>
                    <td className={`px-3 py-3 text-right text-sm ${row.marketGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {row.marketGainLoss >= 0 ? '+' : ''}{formatValue(row.marketGainLoss)}
                    </td>
                    <td className={`px-3 py-3 text-right text-sm ${row.incomeReturns >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatValue(row.incomeReturns)}
                    </td>
                    <td className={`px-3 py-3 text-right text-sm ${row.fees > 0 ? 'text-red-600' : isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {row.fees > 0 ? '-' : ''}{formatValue(Math.abs(row.fees))}
                    </td>
                    <td className={`px-3 py-3 text-right text-sm ${row.personalReturns >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {row.personalReturns >= 0 ? '+' : ''}{formatValue(row.personalReturns)}
                    </td>
                    <td className={`px-3 py-3 text-right text-sm ${row.cumulativeReturns >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {formatValue(row.cumulativeReturns)}
                    </td>
                    <td className={`px-3 py-3 text-right text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {formatValue(row.endingBalance)}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={9} className={`px-3 py-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      No monthly data available. Create wealth snapshots to track month-over-month performance.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Investments Tab */}
        {activeSubTab === 'investments' && (
          <div className="overflow-x-auto">
            {investments.length > 0 ? (
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <th className={`px-3 py-3 text-left text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Investment</th>
                    <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Current Value</th>
                    <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Cost</th>
                    <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Return</th>
                    <th className={`px-3 py-3 text-right text-xs font-semibold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Return %</th>
                  </tr>
                </thead>
                <tbody>
                  {investments.map((inv, idx) => {
                    // Get live price data if ticker exists
                    const priceData = inv.ticker ? prices[inv.ticker] : null;
                    const hasLivePrice = priceData?.price && priceData.price > 0;
                    const quantity = inv.quantity || 0;

                    let currentValue = 0;
                    let costBasis = 0;

                    if (quantity > 0) {
                      // Quantity-tracked investment
                      const avgCost = inv.averageCostBasis || inv.pricePerUnit || 0;
                      let currentPrice;

                      if (hasLivePrice) {
                        currentPrice = priceData.price;
                      } else if (inv.pricePerUnit && inv.pricePerUnit > 0) {
                        currentPrice = inv.pricePerUnit;
                      } else {
                        currentPrice = avgCost;
                      }

                      currentValue = quantity * currentPrice;
                      costBasis = quantity * avgCost;
                    } else {
                      // Amount-only investment
                      currentValue = inv.amount || 0;
                      costBasis = inv.totalCostBasis || inv.amount || 0;
                    }

                    // Convert to display currency if needed
                    const invCurrency = inv.currency || displayCurrency;
                    if (invCurrency !== displayCurrency && exchangeRates?.rates) {
                      const fromRate = exchangeRates.rates[invCurrency] || 1;
                      const toRate = exchangeRates.rates[displayCurrency] || 1;
                      const conversionRate = toRate / fromRate;
                      currentValue *= conversionRate;
                      costBasis *= conversionRate;
                    }

                    const returnAmount = currentValue - costBasis;
                    const returnPercent = costBasis > 0 ? (returnAmount / costBasis) * 100 : 0;

                    return (
                      <tr key={inv.id || idx} className={`border-b ${isDark ? 'border-gray-700 hover:bg-gray-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                        <td className={`px-3 py-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                          <div>
                            <p className="font-medium">
                              {inv.name}
                              {hasLivePrice && <span className="ml-2 text-green-500 text-xs">● Live</span>}
                            </p>
                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {inv.category}
                              {inv.ticker && ` • ${inv.ticker}`}
                              {inv.accountName && ` • ${inv.accountName}`}
                            </p>
                          </div>
                        </td>
                        <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatValue(currentValue)}
                        </td>
                        <td className={`px-3 py-3 text-right text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {formatValue(costBasis)}
                        </td>
                        <td className={`px-3 py-3 text-right text-sm ${returnAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {returnAmount >= 0 ? '+' : ''}{formatValue(returnAmount)}
                        </td>
                        <td className={`px-3 py-3 text-right text-sm ${returnPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {returnPercent >= 0 ? '+' : ''}{returnPercent.toFixed(2)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className={`py-12 text-center ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">No investments yet</p>
                <p className="text-sm mt-1">Add investments to see your portfolio performance here.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceDashboard;
